import classes from '~/config/classes';
import { buildQueryString } from '~/core/query';
import './plans-popup.scss';

import { vueReady } from '~/core/runners';

const selectors = {
  form: '[js-plans-popup="form"]',
  input: '[js-plans-popup="input"]',
  error: '[js-plans-popup="error"]',
  success: '[js-plans-popup="success"]',
  sentError: '[js-plans-popup="sent-error"]',
};

class PlansPopup extends HTMLElement {
  /**
   * Component lifecycle - before mount.
   */
  constructor() {
    super();
    this.$data = {};
  }

  data(component) {
    return {
      selectors: {
        form: component.querySelector(selectors.form),
        input: component.querySelector(selectors.input),
        errorMessage: component.querySelector(selectors.error),
        successMessage: component.querySelector(selectors.success),
        sentErrorMessage: component.querySelector(selectors.sentError),
      },
    };
  }

  /**
   * Component lifecycle - mounted.
   */
  connectedCallback() {
    this.$data = this.data(this);

    this.$data.selectors.form.addEventListener('submit', (event) => {
      event.preventDefault();
      this.handleFormSubmit(event);
    });

    cnvs.EventBus.listen('overlay:close', (namespace) => {
      if (!namespace.includes('plansPopupOverlay')) {
        return;
      }

      this.resetState();
    });
  }

  /**
   * Handle form submit event and message state based on response.
   * @param {SubmitEvent} event - Form submit event
   */
  async handleFormSubmit(event) {
    event.preventDefault();

    const { errorMessage, input } = this.$data.selectors;

    const formData = {
      email: input.value,
      formId: this.dataset.formId,
      listName: this.dataset.listName,
      listId: this.dataset.listId,
    };

    // Check if listName exists in dataset and add it to formData
    if (this.dataset.listName) {
      formData.listName = this.dataset.listName;
    }

    input.classList.remove(classes.success);
    input.classList.remove(classes.error);
    errorMessage.classList.add(classes.hidden);
    errorMessage.setAttribute('aria-hidden', true);

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(formData.email)) {
      this.handleError();
      return;
    }

    if (formData.email === 'error@esn.de') {
      this.handleError();
      return;
    }

    // Mock success and error states
    if (formData.email === 'success@esn.de') {
      this.emailValid();
      return;
    }

    this.emailValid();

    cnvs.EventBus.emit('dataLayer:plan_request', {
      event: 'plan_request',
      planName: this.dataset.listName,
    });

    // Build the query string from form data
    const queryString = buildQueryString(formData);
    // Construct the API URL
    const baseUrl = `${window.location.protocol}//${window.location.host}/apps/custom-klaviyo/`;
    const apiUrl = `${baseUrl}?${queryString}`;

    // Fetch data from the Klaviyo API
    try {
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        this.showResponseError();
        throw new Error('Network response was not ok');
      }

      this.handleSuccess();
    } catch (error) {
      this.handleError();
    }
  }

  /**
   * Show Email valid
   */
  emailValid() {
    const { errorMessage, input } = this.$data.selectors;
    input.classList.remove(classes.error);
    input.classList.add(classes.success);
    errorMessage.classList.add(classes.hidden);
    errorMessage.setAttribute('aria-hidden', true);
  }

  /**
   * Reset form state
   */
  resetState() {
    const { errorMessage, form, successMessage, input, sentErrorMessage } =
      this.$data.selectors;
    form.classList.remove(classes.hidden);
    form.setAttribute('aria-hidden', false);
    input.classList.remove(classes.error);
    input.classList.remove(classes.success);
    errorMessage.classList.add(classes.hidden);
    errorMessage.setAttribute('aria-hidden', true);
    successMessage.classList.add(classes.hidden);
    successMessage.setAttribute('aria-hidden', true);
    sentErrorMessage.classList.add(classes.hidden);
    sentErrorMessage.setAttribute('aria-hidden', true);
  }

  /**
   * Show success message.
   */
  handleSuccess() {
    const { errorMessage, form, successMessage } = this.$data.selectors;
    form.classList.add(classes.hidden);
    form.setAttribute('aria-hidden', true);
    errorMessage.classList.add(classes.hidden);
    errorMessage.setAttribute('aria-hidden', true);
    successMessage.classList.remove(classes.hidden);
    successMessage.setAttribute('aria-hidden', false);
  }

  /**
   * Show response error.
   */
  showResponseError() {
    const { sentError, form, successMessage } = this.$data.selectors;
    form.classList.add(classes.hidden);
    form.setAttribute('aria-hidden', true);
    sentError.classList.remove(classes.hidden);
    sentError.setAttribute('aria-hidden', false);
    successMessage.classList.add(classes.hidden);
    successMessage.setAttribute('aria-hidden', true);
  }

  /**
   * Handle error state.
   */
  handleError() {
    const { errorMessage, input } = this.$data.selectors;
    input.classList.add(classes.error);
    input.classList.remove(classes.success);
    errorMessage.classList.remove(classes.hidden);
    errorMessage.setAttribute('aria-hidden', false);
  }
}

vueReady(() => {
  if (customElements.get('plans-popup')) {
    return;
  }

  customElements.define('plans-popup', PlansPopup);
});
